import { Button, Menu, Popover, Callout, Intent, MenuItem, Classes } from '@blueprintjs/core'
import * as React from 'react'
import { connect } from 'react-redux'
import Client from '../../client'
import { AppState } from '../../store'
import { Note } from '../../types/api'
import InteractiveDateTime from '../InteractiveDatetime'
import Editor from './Editor'
import { NoteMode } from './types'
import { CurrentState } from '../../current/types'
import Widget from '../Widget'

const style = {
  wordBreakAndWrap: {
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    whiteSpace: 'pre-wrap' // Makes the browser break lines at newline characters. Helpful for user-typed notes, and for Hammoq attribute notes.
  } as React.CSSProperties,
  maxWidth: {
    maxWidth: '100%'
  }
}

export interface NoteRendererProps {
  note: Note
  refresh: () => void
  current: CurrentState
  allowEdit: boolean
  sectionLabel?: string
}

interface NoteRendererState {
  loading: boolean
  mode: NoteMode
  noteBody: string
  error: any
}

class NoteRenderer extends React.Component<NoteRendererProps, NoteRendererState> {
  constructor(props) {
    super(props)
    this.state = {
      error: null,
      loading: false,
      mode: NoteMode.View,
      noteBody: props.note.body
    }
  }

  deleteNote = async () => {
    this.setState({ loading: true })
    try {
      await Client.deleteNote(this.props.note.id)
      this.setState(
        {
          loading: false
        },
        this.props.refresh
      )
    } catch (e) {
      this.setState({ error: 'There was an error deleting that note, please try again', loading: false })
    }
  }

  setEditMode = () => {
    this.setState({
      mode: NoteMode.Edit
    })
  }

  onEditBody = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      noteBody: e.target.value
    })
  }

  onSubmit = async () => {
    this.setState({ loading: true, error: null })
    try {
      await Client.updateNote(this.props.note.id, { body: this.state.noteBody })
      this.setState({ loading: false, mode: NoteMode.View }, this.props.refresh)
    } catch (error) {
      this.setState({ error: 'There was an error updating that note, please try again', loading: false })
    }
  }

  onCancel = () => {
    this.setState({ mode: NoteMode.View, noteBody: this.props.note.body })
  }

  render() {
    const { note, current } = this.props
    const { loading, mode, noteBody, error } = this.state

    let userNameLabel = null
    if (note.user) {
      userNameLabel = note.user.employee ? 'Upright Labs' : note.user.username
    }
    const userSupplierLabel = note.user?.supplier?.name ? `(${note.user.supplier.name})` : ''
    const fullAuthorLabel = `${[userNameLabel, userSupplierLabel].join(' ')}`

    const hasActions =
      this.props.current.user.permissions.includes('misc.edit_notes') || current.user.id === note.user.id

    return (
      <>
        <Widget
          loading={loading}
          style={{ marginBottom: 20 }}
          title={
            <div style={{ fontWeight: 'normal', display: 'flex', alignItems: 'center' }}>
              <p style={{ margin: 0 }}>
                {fullAuthorLabel}{' '}
                <span className={Classes.TEXT_MUTED}>
                  commented on <InteractiveDateTime value={note.createdAt} />
                </span>
              </p>

              {hasActions && this.props.allowEdit && (
                <div style={{ marginLeft: 'auto' }}>
                  <Popover
                    content={
                      <Menu>
                        <MenuItem icon="edit" onClick={this.setEditMode} text="Edit" />
                        <MenuItem icon="trash" onClick={this.deleteNote} text="Delete" />
                      </Menu>
                    }
                  >
                    <Button icon="more" minimal small />
                  </Popover>
                </div>
              )}
            </div>
          }
        >
          {error && (
            <Callout intent={Intent.DANGER} title="Unexpected error">
              <p>{error}</p>
              <Button onClick={this.props.refresh} intent={Intent.DANGER}>
                Try again
              </Button>
            </Callout>
          )}

          <div style={style.maxWidth}>
            {mode === NoteMode.View ? (
              <p style={style.wordBreakAndWrap}>{note.body}</p>
            ) : (
              <Editor
                onCancel={this.onCancel}
                onChange={this.onEditBody}
                editMode
                onSubmit={this.onSubmit}
                value={noteBody}
              />
            )}
          </div>
        </Widget>
      </>
    )
  }
}

export default connect((state: AppState) => ({ current: state.current }))(NoteRenderer)
